import { apiClient } from './api';

// Enums
export enum EmailDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound'
}

export enum EmailStatus {
  Pending = 'Pending',
  Success = 'Success',
  Failed = 'Failed',
  Blocked = 'Blocked',
  Spam = 'Spam'
}

export enum EmailType {
  Webhook = 'Webhook',
  SMTP = 'SMTP',
  API = 'API'
}

export enum WebhookValidationStatus {
  Valid = 'Valid',
  Invalid = 'Invalid',
  NotValidated = 'NotValidated'
}

export enum IpValidationStatus {
  Valid = 'Valid',
  Invalid = 'Invalid',
  Localhost = 'Localhost',
  Unknown = 'Unknown'
}

// DTOs
export interface EmailLogDto {
  id: string;
  direction: EmailDirection;
  type: EmailType;
  endpoint: string;
  fromEmail: string;
  fromName?: string;
  toEmails: string;
  ccEmails?: string;
  bccEmails?: string;
  subject: string;
  status: EmailStatus;
  processingTimeMs?: number;
  errorDetails?: string;
  errorType?: string;
  ipAddress?: string;
  userAgent?: string;
  ticketId?: string;
  ticketNumber?: string;
  userId?: string;
  userName?: string;
  externalMessageId?: string;
  signatureValidation?: WebhookValidationStatus;
  ipValidation?: IpValidationStatus;
  spamScore?: number;
  spamReasons?: string;
  attachmentCount?: number;
  attachmentSizeBytes?: number;
  newCustomerCreated?: boolean;
  newTicketCreated?: boolean;
  environment?: string;
  serverName?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
  headers?: Record<string, string>;
  requestDataSummary?: string;
  responseDataSummary?: string;
}

export interface EmailLogStatsDto {
  totalEmails: number;
  successfulEmails: number;
  failedEmails: number;
  spamEmails: number;
  blockedEmails: number;
  inboundEmails: number;
  outboundEmails: number;
  averageProcessingTimeMs: number;
  topEndpoints: Array<{
    endpoint: string;
    count: number;
    successRate: number;
  }>;
  errorsByType: Array<{
    errorType: string;
    count: number;
  }>;
  successRateByDay: Array<{
    date: string;
    total: number;
    successful: number;
    successRate: number;
  }>;
}

export interface SpamDetectionSummaryDto {
  totalSpam: number;
  averageSpamScore: number;
  topSpamReasons: Array<{
    reason: string;
    count: number;
  }>;
  spamByDay: Array<{
    date: string;
    count: number;
  }>;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
  errors?: string[];
}

export interface EmailLogFilters {
  startDate?: string;
  endDate?: string;
  direction?: EmailDirection;
  status?: EmailStatus;
  endpoint?: string;
  fromEmail?: string;
  toEmail?: string;
  ticketId?: string;
  page?: number;
  pageSize?: number;
}

export interface AddMetadataRequest {
  key: string;
  value: any;
}

class EmailLogService {
  private readonly baseUrl = '/emaillog';

  /**
   * Get paginated email logs with filtering
   */
  async getEmailLogs(filters: EmailLogFilters = {}): Promise<PaginatedResponse<EmailLogDto>> {
    const params = new URLSearchParams();

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.direction) params.append('direction', filters.direction);
    if (filters.status) params.append('status', filters.status);
    if (filters.endpoint) params.append('endpoint', filters.endpoint);
    if (filters.fromEmail) params.append('fromEmail', filters.fromEmail);
    if (filters.toEmail) params.append('toEmail', filters.toEmail);
    if (filters.ticketId) params.append('ticketId', filters.ticketId);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

    const response = await apiClient.get<PaginatedResponse<EmailLogDto>>(`${this.baseUrl}?${params.toString()}`);
    return response.data;
  }

  /**
   * Get email log by ID
   */
  async getEmailLogById(id: string): Promise<EmailLogDto> {
    const response = await apiClient.get<ApiResponse<EmailLogDto>>(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  /**
   * Get email logs for a specific ticket
   */
  async getEmailLogsByTicket(ticketId: string): Promise<EmailLogDto[]> {
    const response = await apiClient.get<ApiResponse<EmailLogDto[]>>(`${this.baseUrl}/tickets/${ticketId}`);
    return response.data.data;
  }

  /**
   * Get email log statistics
   */
  async getEmailLogStatistics(startDate?: string, endDate?: string): Promise<EmailLogStatsDto> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await apiClient.get<ApiResponse<EmailLogStatsDto>>(`${this.baseUrl}/statistics?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get recent failed email attempts
   */
  async getRecentFailedEmails(hours: number = 24, limit: number = 50): Promise<EmailLogDto[]> {
    const params = new URLSearchParams({
      hours: hours.toString(),
      limit: limit.toString()
    });

    const response = await apiClient.get<ApiResponse<EmailLogDto[]>>(`${this.baseUrl}/failed?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get spam detection summary
   */
  async getSpamDetectionSummary(startDate?: string, endDate?: string): Promise<SpamDetectionSummaryDto> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await apiClient.get<ApiResponse<SpamDetectionSummaryDto>>(`${this.baseUrl}/spam/summary?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Export email logs to CSV
   */
  async exportEmailLogs(filters: EmailLogFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.direction) params.append('direction', filters.direction);
    if (filters.status) params.append('status', filters.status);

    const response = await apiClient.axiosClient.get(`${this.baseUrl}/export?${params.toString()}`, {
      responseType: 'blob'
    });

    return response.data;
  }

  /**
   * Add metadata to email log (Admin only)
   */
  async addMetadata(id: string, key: string, value: any): Promise<void> {
    const request: AddMetadataRequest = { key, value };
    await apiClient.post<ApiResponse<string>>(`${this.baseUrl}/${id}/metadata`, request);
  }

  /**
   * Helper method to format processing time
   */
  formatProcessingTime(ms?: number): string {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }

  /**
   * Helper method to format file size
   */
  formatFileSize(bytes?: number): string {
    if (!bytes) return 'N/A';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  /**
   * Helper method to get status badge color
   */
  getStatusColor(status: EmailStatus): string {
    switch (status) {
      case EmailStatus.Success:
        return 'bg-green-100 text-green-800';
      case EmailStatus.Failed:
        return 'bg-red-100 text-red-800';
      case EmailStatus.Spam:
        return 'bg-yellow-100 text-yellow-800';
      case EmailStatus.Blocked:
        return 'bg-red-100 text-red-800';
      case EmailStatus.Pending:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Helper method to get direction color
   */
  getDirectionColor(direction: EmailDirection): string {
    switch (direction) {
      case EmailDirection.Inbound:
        return 'bg-blue-100 text-blue-800';
      case EmailDirection.Outbound:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
}

export const emailLogService = new EmailLogService();